import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingBag, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

const ProductDetail = () => {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [colorTouched, setColorTouched] = useState(false);
  const [qty, setQty] = useState(1);
  const [monogram, setMonogram] = useState("");
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setSize((r.data.sizes || [])[0] || "");
      setColor((r.data.colors || [])[0] || "");
      setColorTouched(false);
      setActiveImage(0);
    });
  }, [slug]);

  // Swap image ONLY when the buyer actively picks a color. On initial load,
  // always show images[0] (the "MAIN" photo the admin set).
  useEffect(() => {
    if (!product || !color || !colorTouched) return;
    const imgs = product.images || [];
    const map = product.color_images || {};
    let target = map[color] || map[color.toLowerCase()];
    if (!target) {
      const needle = color.toLowerCase();
      target = imgs.find((src) => src.toLowerCase().includes(needle));
    }
    if (target) {
      const idx = imgs.indexOf(target);
      if (idx >= 0) setActiveImage(idx);
    }
  }, [color, colorTouched, product]);

  if (!product) {
    return <div className="max-w-6xl mx-auto px-5 py-20 text-[#333333]">Loading...</div>;
  }

  const handleAdd = () => {
    addItem(product, {
      size,
      color,
      quantity: qty,
      monogram: product.monogram_enabled ? (monogram.trim().toUpperCase().slice(0, 3) || null) : null,
      monogram_price: product.monogram_enabled ? Number(product.monogram_price || 0) : 0,
    });
    toast.success(`Added ${product.name} to bag`);
  };

  return (
    <main data-testid="product-page" className="max-w-6xl mx-auto px-5 py-12">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm font-bold text-[#333333] hover:text-[#000000] mb-6">
        <ArrowLeft size={16} /> Back to shop
      </Link>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          {(() => {
            const media = [
              ...(product.images || []).map((src) => ({ type: "image", src })),
              ...(product.video ? [{ type: "video", src: product.video }] : []),
            ];
            const current = media[activeImage] || media[0];
            return (
              <>
                <div className="brutal bg-[#F7F7F5] rounded-3xl overflow-hidden aspect-[4/5] flex items-center justify-center relative">
                  {current && current.type === "video" ? (
                    <video
                      data-testid="product-video-player"
                      key={current.src}
                      src={current.src}
                      className="w-full h-full object-contain bg-black"
                      controls
                      autoPlay
                      playsInline
                      preload="metadata"
                    />
                  ) : current ? (
                    <img src={current.src} alt={product.name} className="w-full h-full object-contain" />
                  ) : null}
                </div>
                {media.length > 1 && (
                  <div className="mt-4 flex gap-3 flex-wrap">
                    {media.map((m, i) => (
                      <button
                        key={i}
                        data-testid={m.type === "video" ? "product-thumb-video" : `product-thumb-${i}`}
                        onClick={() => setActiveImage(i)}
                        className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 bg-[#F7F7F5] flex items-center justify-center ${
                          activeImage === i ? "border-[#EC008C] ring-2 ring-[#EC008C]" : "border-[#000000]"
                        }`}
                      >
                        {m.type === "video" ? (
                          <>
                            <video
                              src={m.src}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                                <span className="border-l-[8px] border-l-black border-y-[6px] border-y-transparent ml-1"></span>
                              </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-[#EC008C] text-white text-[9px] tracking-widest uppercase font-black text-center py-0.5">
                              Video
                            </div>
                          </>
                        ) : (
                          <img src={m.src} alt="" className="w-full h-full object-contain" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#EC008C]">{product.category}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl text-[#000000] mt-2 leading-tight">
            {product.name}
          </h1>
          <div className="font-display font-black text-3xl text-[#000000] mt-4">${product.price.toFixed(2)}</div>
          <p className="mt-5 text-[#333333] leading-relaxed whitespace-pre-line">{product.description}</p>

          {product.processing_time && (
            <div data-testid="product-processing-time" className="mt-4 border-2 border-black bg-[#FFFBEA] px-4 py-2.5 text-xs">
              <span className="text-[10px] tracking-[0.24em] uppercase font-black text-black">Processing time</span>
              <div className="text-[#333] whitespace-pre-line">{product.processing_time}</div>
            </div>
          )}

          {product.preorder && (
            <div data-testid="product-preorder-notice" className="mt-4 border-2 border-black bg-[#FFF0F8] px-4 py-3 text-sm">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#EC008C]">Pre-Order</div>
              <div className="text-[#333] mt-1 font-semibold">{product.preorder_note || "This item is available for pre-order."}</div>
              {product.preorder_date && (
                <div className="text-[#555] text-xs mt-1">Estimated {product.preorder_fulfillment === "pickup" ? "pickup" : "ship"} date: {product.preorder_date}</div>
              )}
              <div className="text-[#555] text-xs mt-1">Full payment is collected now via secure checkout.</div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-bold uppercase tracking-widest text-[#000000] mb-2">Color</div>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-${c}`}
                    onClick={() => { setColor(c); setColorTouched(true); }}
                    className={`px-4 py-2 rounded-full border-2 border-[#000000] text-sm font-semibold ${
                      color === c ? "bg-[#000000] text-[#FFFFFF]" : "bg-white text-[#000000]"
                    }`}
                  >
                    {color === c && <Check size={14} className="inline mr-1" />}
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-5">
              <div className="text-sm font-bold uppercase tracking-widest text-[#000000] mb-2">Size</div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-${s}`}
                    onClick={() => setSize(s)}
                    className={`min-w-[52px] px-4 py-2 rounded-full border-2 border-[#000000] text-sm font-semibold ${
                      size === s ? "bg-[#EC008C] text-[#FFFFFF]" : "bg-white text-[#000000]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.monogram_enabled && (
            <div className="mt-5" data-testid="product-monogram">
              <div className="text-sm font-bold uppercase tracking-widest text-[#000000] mb-2">
                Monogram <span className="text-[#EC008C]">{Number(product.monogram_price || 0) > 0 ? `+$${Number(product.monogram_price).toFixed(2)}` : "· Free"}</span>
              </div>
              <p className="text-xs text-[#555] mb-2">Add up to 3 initials. Leave blank to skip.</p>
              <input
                data-testid="product-monogram-input"
                value={monogram}
                onChange={(e) => setMonogram(e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 3))}
                placeholder="e.g. MSG"
                maxLength={3}
                className="w-36 px-4 py-2.5 border-2 border-black rounded-full font-black tracking-[0.3em] text-center uppercase"
              />
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center brutal-static bg-white rounded-full overflow-hidden">
              <button
                data-testid="qty-minus"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-10 h-10 text-lg font-bold"
              >
                −
              </button>
              <div data-testid="qty-value" className="w-10 text-center font-bold">
                {qty}
              </div>
              <button
                data-testid="qty-plus"
                onClick={() => setQty(qty + 1)}
                className="w-10 h-10 text-lg font-bold"
              >
                +
              </button>
            </div>
          </div>

          <button
            data-testid="add-to-cart-btn"
            onClick={handleAdd}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#EC008C] text-[#FFFFFF] font-display font-bold text-lg px-8 py-4 rounded-full brutal-lg"
          >
            <ShoppingBag size={20} /> Add to Bag
          </button>

          <div className="mt-8 grid grid-cols-3 gap-3 text-xs text-[#333333]">
            <div className="brutal-static bg-white p-3 rounded-xl">📦 Made to order</div>
            <div className="brutal-static bg-white p-3 rounded-xl">🇺🇸 Printed in OKC</div>
            <div className="brutal-static bg-white p-3 rounded-xl">💛 Small biz love</div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
